import React from 'react';
import { Alert, Tooltip, Typography } from '@mui/material';
import { DevicePhoneMobileIcon, FlagIcon, SignalSlashIcon } from '@heroicons/react/24/solid';
import { ActivityFeedRow } from 'src/@types/reports';
import { useOrg } from 'src/hooks/query/orgHooks';
import { formatCanvassingDistance } from 'src/utils/formatCanvassingDistance';

const MILE = 5280;
const ERROR_DISTANCE = MILE * 5;
const WARNING_DISTANCE = MILE * 1;

type CanvassDistanceAlertProps = {
  activityFeedRow: ActivityFeedRow;
};

const CanvassDistanceAlert = ({ activityFeedRow }: CanvassDistanceAlertProps) => {
  const { data: org } = useOrg();

  if (activityFeedRow.type?.toUpperCase() !== 'CANVASS') {
    return <></>;
  }

  const hasErrorDistanceEnabled = org?.canvassing_qa_distance_from_door_enabled;
  const errorDistance =
    hasErrorDistanceEnabled && org ? org.canvassing_qa_distance_from_door_feet : WARNING_DISTANCE;

  const {
    delta_feet: deltaFeet,
    is_using_emulator: isUsingEmulator,
    device_geolocation_enabled: deviceGeolocationEnabled,
    device_geolocation_permission_status: deviceGeolocationPermissionStatus,
  } = activityFeedRow;

  let tooltipMsg = '';
  let severity: 'info' | 'error' | 'warning' = 'info';
  let alertMsg: string | React.ReactElement = '';
  let icon: React.ReactElement = <FlagIcon width={20} height={20} />;

  if (isUsingEmulator) {
    tooltipMsg = '';
    severity = 'error';
    alertMsg = 'Canvasser is using a device emulator';
    icon = <DevicePhoneMobileIcon width={20} height={20} />;
  } else if (deltaFeet && deltaFeet > errorDistance) {
    tooltipMsg = `Approximate distance: ${formatCanvassingDistance(deltaFeet)}`;
    severity = 'warning';
    if (hasErrorDistanceEnabled) {
      alertMsg = `Canvasser was over ${org?.canvassing_qa_distance_from_door_feet} feet from address`;
    } else if (deltaFeet > WARNING_DISTANCE) {
      alertMsg = 'Canvasser was over 1 mile from address';
    } else if (deltaFeet > ERROR_DISTANCE) {
      alertMsg = 'Canvasser was over 5 miles from address';
    }
    icon = <></>;
  } else if (!!deviceGeolocationPermissionStatus && deviceGeolocationEnabled === false) {
    tooltipMsg = '';
    severity = 'error';
    alertMsg = 'Canvasser has GPS location turned off';
    icon = <SignalSlashIcon width={20} height={20} />;
  } else if (
    deviceGeolocationPermissionStatus !== 'granted' &&
    !!deviceGeolocationPermissionStatus
  ) {
    tooltipMsg = '';
    severity = 'error';
    alertMsg = (
      <>
        Canvasser has limited GPS permission. App GPS permission status:{' '}
        <b>
          {deviceGeolocationPermissionStatus.charAt(0).toUpperCase() +
            deviceGeolocationPermissionStatus.slice(1)}
        </b>
      </>
    );
    icon = <SignalSlashIcon width={20} height={20} />;
  } else if (!deltaFeet) {
    tooltipMsg = '';
    severity = 'info';
    alertMsg = 'Canvasser GPS location not available';
  } else {
    return <></>;
  }

  return (
    <Tooltip title={tooltipMsg} arrow>
      <Alert
        severity={severity}
        icon={icon}
        sx={{
          alignItems: 'center',
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: '6px',
          '.MuiAlert-icon': {
            marginRight: '6px',
          },
        }}
      >
        <Typography variant="caption">{alertMsg}</Typography>
      </Alert>
    </Tooltip>
  );
};

export default CanvassDistanceAlert;
